import * as React from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { useForm, useStore } from '@tanstack/react-form'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'

import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { getErrorMessage, reportError } from '@/lib/error-reporting'

import { RichTextEditor } from '@/components/shared/rich-text-editor'

import type { Invoice, Item, InvoiceLineItem, PaymentSchedule } from '@/gen/cortado/v1/invoices_pb'
import {
  DiscountTypeEnum,
  InvoicePaymentScheduleAdjustmentStrategyEnum,
  InvoiceStatusEnum,
  ItemUnitEnum,
  PaymentScheduleStatusEnum,
  StripeCheckoutPaymentMethodEnum,
  type TipAllocationModeEnum,
} from '@/gen/cortado/v1/settings_pb'
import {
  CreateInvoiceRequestSchema,
  UpdateInvoiceRequestSchema,
  UpdatePaymentScheduleRequestSchema,
  ReplacePaymentSchedulesRequestSchema,
  PaymentScheduleInputSchema,
} from '@/gen/cortado/v1/cortado_pb'
import { QuoteLineItemInputSchema } from '@/gen/cortado/v1/invoices_pb'
import { QuoteLineItemsTable, type QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import { InvoicePaymentScheduleEditor } from './invoice-payment-schedule-editor'
import { PaymentOverviewCard } from './payment-overview-card'
import { PaymentHistory } from './payment-history'
import { RecordPaymentDialog } from './record-payment-dialog'
import { RefundDialog } from './refund-dialog'
import { VoidPaymentDialog } from './void-payment-dialog'
import { PaymentScheduleAdjustmentDialog } from './payment-schedule-adjustment-dialog'
import { getInFlightPaymentSchedules, useInvoicePayments, useReplacePaymentSchedules, useUpdatePaymentSchedule } from '@/stores/payments.store'
import type { ManualPayment, StripePayment } from '@/gen/cortado/v1/invoices_pb'
import { useCurrentOrganization, useListDiscounts, useListItems, useListTaxRates, useOrgTipAllocationModeDefault } from '@/stores/organization.store'
import { TipAllocationModeSelect } from '@/components/shared/financial-document/tip-allocation-mode-select'
import { normalizeTipAllocationMode } from '@/components/shared/financial-document/tip-allocation-mode'
import { dollarsToCents, formatCurrency } from '@/lib/currency-utils'
import { stringToItemUnitEnum, itemUnitEnumToString } from '@/lib/enum-utils'
import { cn } from '@/lib/utils'
import { roundTipPercentage } from '@/lib/tip-percentage'
import {
  areInvoicePaymentScheduleFormValuesEqual,
  appendInvoicePaymentScheduleForTotal,
  getInvoicePaymentScheduleLockReason,
  hasActiveInvoiceCheckoutSession,
  invoicePaymentSchedulesToFormValues,
  isInvoicePaymentScheduleLockedForEditing,
  formValuesToInvoicePaymentSchedules,
  planInvoicePaymentScheduleSync,
  rebalanceInvoicePaymentSchedulesForTotal,
  type InvoicePaymentScheduleFormValue,
} from '@/lib/payment-schedule-utils'
import { useCreateInvoice, useUpdateInvoice, hasInvoiceLineItemsChanged } from '@/stores/invoices.store'
import { dateToDateOnlyTimestamp, dateToTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import { useFinancialDocumentTotals } from '@/hooks/use-financial-document-totals'
import { useEventsList } from '@/stores/events.store'
import { TransportationLineItemSection } from '@/components/quotes/transportation-line-item-section'
import {
  replacePrimaryLineItems,
  withoutTransportationLineItems,
} from '@/lib/transportation-line-items'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/components/ui/input-group'
import { AlertTriangle, Loader2, Send } from 'lucide-react'
import {
  FinancialDocumentDetailsFields,
  QuickTaxSelector,
  FinancialDocumentTotalsBreakdown,
} from '@/components/shared/financial-document'
import { QuoteBuilderSummaryRail } from '@/components/quotes/quote-builder-summary-rail'
import { InvoiceEditorPreview } from './invoice-editor-preview'
import {
  getInvoiceEditorChecklist,
  type InvoiceEditorSectionId,
} from './invoice-editor-readiness'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'

interface InvoiceEditorProps {
  invoice: Invoice
  organizationId?: string
  availableItems?: Item[]
  initialIsEditing?: boolean
  onCancel?: () => void
  onSuccess?: (invoiceId?: string) => void
  /**
   * @deprecated No longer read — the payment cards check PaymentsManage
   * themselves via ActionGate so a denied user gets a disabled control with an
   * explanation instead of a vanished one. Kept only until the remaining
   * caller (`components/events/event-invoices-card.tsx`) drops it.
   */
  canManagePayments?: boolean
}

const DEFAULT_CHECKOUT_PAYMENT_METHODS = [
  StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_CARD,
  StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_US_BANK_ACCOUNT,
]

const INVOICE_EDITOR_SECTION_DOM_IDS: Record<InvoiceEditorSectionId, string> = {
  details: 'invoice-details',
  items: 'invoice-items',
  payment: 'invoice-payment-schedule-tab-content',
}

function arePaymentMethodsEqual(
  current: StripeCheckoutPaymentMethodEnum[] | undefined,
  next: StripeCheckoutPaymentMethodEnum[],
): boolean {
  const currentMethods = current ?? []
  if (currentMethods.length !== next.length) return false
  return currentMethods.every((method, index) => method === next[index])
}

function formItemToInvoiceLineItem(item: QuoteLineItem): InvoiceLineItem {
  return {
    sourceItemId: item.sourceItemId,
    name: item.name,
    quantity: item.quantity,
    unitPriceCents: BigInt(item.unitPriceCents),
    description: item.description,
    unit: item.unit ? itemUnitEnumToString(item.unit) : '',
    sortOrder: item.sortOrder,
    isTaxable: item.isTaxable,
    salesTaxRateId: item.salesTaxRateId,
    salesTaxRateName: item.salesTaxRateName,
    salesTaxRate: item.salesTaxRate,
    salesTaxCents: BigInt(item.salesTaxCents || 0),
  } as unknown as InvoiceLineItem
}

function normalizeInvoicePaymentSchedules(
  schedules: InvoicePaymentScheduleFormValue[],
  invoiceTotalCents: number,
): InvoicePaymentScheduleFormValue[] {
  if (schedules.length > 0) return schedules

  return [{
    sequenceNumber: 1,
    description: 'Full Payment',
    dueDate: new Date(),
    amountCents: invoiceTotalCents,
  }]
}

function getPaymentScheduleTotalMismatch(
  schedules: InvoicePaymentScheduleFormValue[],
  invoiceTotalCents: number,
): { scheduleTotalCents: number; message: string } | undefined {
  if (schedules.length === 0) return undefined

  const scheduleTotalCents = schedules.reduce((sum, s) => sum + (s.amountCents || 0), 0)
  if (scheduleTotalCents === invoiceTotalCents) return undefined

  return {
    scheduleTotalCents,
    message: scheduleTotalCents < invoiceTotalCents
      ? `Payment schedule total ($${formatCurrency(scheduleTotalCents)}) is less than invoice total ($${formatCurrency(invoiceTotalCents)})`
      : `Payment schedule total ($${formatCurrency(scheduleTotalCents)}) exceeds invoice total ($${formatCurrency(invoiceTotalCents)})`,
  }
}

function haveInvoicePaymentSchedulesChanged(
  currentSchedules: PaymentSchedule[],
  formSchedules: InvoicePaymentScheduleFormValue[],
): boolean {
  return !areInvoicePaymentScheduleFormValuesEqual(
    invoicePaymentSchedulesToFormValues(currentSchedules),
    formSchedules,
  )
}

function getPaymentScheduleAdjustmentResult(
  currentSchedules: PaymentSchedule[],
  invoiceTotalCents: number,
  strategy: InvoicePaymentScheduleAdjustmentStrategyEnum,
): InvoicePaymentScheduleFormValue[] {
  const currentFormSchedules = invoicePaymentSchedulesToFormValues(currentSchedules)
  if (strategy === InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_NEW_PAYMENT) {
    return appendInvoicePaymentScheduleForTotal(currentFormSchedules, invoiceTotalCents)
  }

  return rebalanceInvoicePaymentSchedulesForTotal(
    currentSchedules,
    currentFormSchedules,
    invoiceTotalCents,
  )
}

function isOnlyPaymentScheduleAdjustmentChoiceChange(
  currentSchedules: PaymentSchedule[],
  formSchedules: InvoicePaymentScheduleFormValue[],
  invoiceTotalCents: number,
  strategy: InvoicePaymentScheduleAdjustmentStrategyEnum,
): boolean {
  if (strategy === InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN) {
    return false
  }

  return areInvoicePaymentScheduleFormValuesEqual(
    getPaymentScheduleAdjustmentResult(currentSchedules, invoiceTotalCents, strategy),
    formSchedules,
  )
}

function inferPaymentScheduleAdjustmentStrategy(
  currentSchedules: PaymentSchedule[],
  formSchedules: InvoicePaymentScheduleFormValue[],
  invoiceTotalCents: number,
  selectedStrategy: InvoicePaymentScheduleAdjustmentStrategyEnum,
): InvoicePaymentScheduleAdjustmentStrategyEnum {
  if (selectedStrategy !== InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN) {
    return selectedStrategy
  }

  if (areInvoicePaymentScheduleFormValuesEqual(
    getPaymentScheduleAdjustmentResult(
      currentSchedules,
      invoiceTotalCents,
      InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_LAST_PAYMENT,
    ),
    formSchedules,
  )) {
    return InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_LAST_PAYMENT
  }

  if (areInvoicePaymentScheduleFormValuesEqual(
    getPaymentScheduleAdjustmentResult(
      currentSchedules,
      invoiceTotalCents,
      InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_NEW_PAYMENT,
    ),
    formSchedules,
  )) {
    return InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_NEW_PAYMENT
  }

  return InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN
}

// True when the payment schedule diff can't be explained by re-applying the
// effective adjustment strategy alone - i.e. the user hand-edited schedule
// rows rather than just changing the invoice total.
function getPaymentSchedulesChangedByUser(
  paymentSchedulesChanged: boolean,
  currentSchedules: PaymentSchedule[],
  formSchedules: InvoicePaymentScheduleFormValue[],
  invoiceTotalCents: number,
  effectiveStrategy: InvoicePaymentScheduleAdjustmentStrategyEnum,
): boolean {
  if (!paymentSchedulesChanged) return false

  const changedByStrategy = isOnlyPaymentScheduleAdjustmentChoiceChange(
    currentSchedules,
    formSchedules,
    invoiceTotalCents,
    effectiveStrategy,
  )
  return !changedByStrategy
}

function hasActiveCheckoutSession(schedule: PaymentSchedule, nowMs = Date.now()): boolean {
  return hasActiveInvoiceCheckoutSession(schedule, nowMs)
}

function isPaymentSchedulePaid(schedule: PaymentSchedule): boolean {
  return Number(schedule.amountPaidCents || 0n) > 0 ||
    schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID
}

function invoiceScheduleDateKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function paymentScheduleDueDate(schedule: PaymentSchedule): Date {
  return timestampToDateOnly(schedule.dueDate) ?? new Date()
}

// Guards the manual-edit path on an invoice that already has a paid schedule
// row. Amounts on UNPAID rows are hand-editable (the save path sends only the
// editable rows to ReplacePaymentSchedules), and unpaid rows may be added or
// removed (e.g. splitting the final installment) as long as the total holds;
// paid rows and reordering existing rows stay locked.
export function getPaidInvoiceScheduleManualChangeError(
  currentSchedules: PaymentSchedule[],
  formSchedules: InvoicePaymentScheduleFormValue[],
): string | undefined {
  // Added or removed unpaid rows change the editable row set just like an
  // amount edit, so they get the same in-flight/locked-row protection below.
  let editableAmountChanged = formSchedules.some((schedule) => !schedule.id)

  for (const currentSchedule of currentSchedules) {
    const formSchedule = formSchedules.find((schedule) => schedule.id === currentSchedule.id)
    if (!formSchedule) {
      if (isPaymentSchedulePaid(currentSchedule)) {
        return 'Paid payment items cannot be removed.'
      }
      editableAmountChanged = true
      continue
    }

    const dueDateChanged = invoiceScheduleDateKey(paymentScheduleDueDate(currentSchedule)) !==
      invoiceScheduleDateKey(formSchedule.dueDate)
    const descriptionChanged = (currentSchedule.description || '') !== (formSchedule.description || '')
    const amountChanged = Number(currentSchedule.amountCents || 0n) !== (formSchedule.amountCents || 0)
    const sequenceChanged = currentSchedule.sequenceNumber !== formSchedule.sequenceNumber

    if (isPaymentSchedulePaid(currentSchedule) &&
      (dueDateChanged || descriptionChanged || amountChanged || sequenceChanged)) {
      return 'Paid payment items cannot be edited.'
    }

    if (sequenceChanged) {
      return 'Payment items cannot be reordered once a payment has been recorded. Edit their amounts instead.'
    }

    if (amountChanged) {
      editableAmountChanged = true
    }
  }

  // Replacing the editable rows is rejected server-side while any row on the
  // invoice is mid-payment or otherwise no longer editable, so say so here
  // instead of dead-ending on the raw error.
  if (editableAmountChanged) {
    for (const currentSchedule of currentSchedules) {
      if (isPaymentSchedulePaid(currentSchedule)) continue

      if (currentSchedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAYMENT_INITIATED) {
        return 'A payment is being processed on this invoice. Wait for it to finish before changing payment amounts.'
      }

      if (isInvoicePaymentScheduleLockedForEditing(currentSchedule)) {
        return 'A payment item on this invoice can no longer be edited, so amounts cannot be changed here.'
      }
    }
  }

  return undefined
}

// Helper to safely convert discount type, filtering out UNKNOWN
function sanitizeDiscountType(
  discountType: DiscountTypeEnum | undefined
): DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE | DiscountTypeEnum.DISCOUNT_TYPE_FIXED | undefined {
  if (discountType === undefined || discountType === DiscountTypeEnum.DISCOUNT_TYPE_UNKNOWN) {
    return undefined
  }
  return discountType as DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE | DiscountTypeEnum.DISCOUNT_TYPE_FIXED
}

// Helper to build the update request so we can expose it to parent components if needed
export function buildUpdateRequest(
  invoice: Invoice,
  values: InvoiceFormValues,
  descriptionHtml: string | undefined,
  paymentScheduleAdjustmentStrategy = InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN,
) {
  const paths: string[] = []

  // Normalize empty values for comparison (undefined and empty string are treated the same)
  const normalizeDiscountId = (val: string | undefined) => val || ''

  if (values.title !== invoice.title) {
    paths.push('title')
  }

  if ((descriptionHtml || '') !== (invoice.description || '')) {
    paths.push('description')
  }

  // Convert tipAmount from dollars to cents for comparison
  const currentTipCents = Number(invoice.tipCents || 0n)
  const newTipCents = Math.round(values.tipAmount * 100)
  if (newTipCents !== currentTipCents) {
    paths.push('tip_cents')
  }

  // Compare enforced tip percentage (also update enabled flag when percentage changes)
  const currentEnforcedTipPercentage = invoice.enforcedTipPercentage || 0
  if (values.enforcedTipPercentage !== currentEnforcedTipPercentage) {
    paths.push('enforced_tip_percentage')
    paths.push('enforced_tip_enabled')
  }

  // Tip collection mode: independent of the tip amount, normalized so an unset mode equals LAST_SCHEDULE
  const nextTipAllocationMode = normalizeTipAllocationMode(values.tipAllocationMode)
  if (nextTipAllocationMode !== normalizeTipAllocationMode(invoice.tipAllocationMode)) {
    paths.push('tip_allocation_mode')
  }

  // Check if discount has been changed from initial state (saved or inline)
  const hasDiscountChanged =
    normalizeDiscountId(values.discountId) !== normalizeDiscountId(invoice.discountId) ||
    values.discountName !== invoice.discountName ||
    values.discountType !== invoice.discountType ||
    values.discountValue !== invoice.discountValue

  if (hasDiscountChanged) {
    paths.push('discount_id', 'discount_name', 'discount_type', 'discount_value')
  }

  const nextCheckoutPaymentMethods = values.useCustomPaymentMethods
    ? values.checkoutPaymentMethods
    : []
  if (!arePaymentMethodsEqual(invoice.checkoutPaymentMethods, nextCheckoutPaymentMethods)) {
    paths.push('checkout_payment_methods')
  }

  // Check if line items have changed — convert form items to InvoiceLineItem shape for comparison
  const formItemsAsLineItems = values.items.map(formItemToInvoiceLineItem)

  const lineItemsChanged = hasInvoiceLineItemsChanged(invoice.lineItems, formItemsAsLineItems)
  if (lineItemsChanged) {
    paths.push('line_items')
    paths.push('revision_reason')
  }

  // Note: Payment schedules are NOT included in UpdateInvoiceRequest
  // They are managed separately via CreatePaymentSchedule/UpdatePaymentSchedule/DeletePaymentSchedule APIs
  // The payment schedule form field is only used for CreateInvoice and for display purposes during edit

  const mask = paths.length
    ? create(FieldMaskSchema, { paths })
    : undefined

  // Convert line items to proto format (same pattern as create flow)
  const lineItems = lineItemsChanged
    ? values.items.map((item) => {
        const isCustomItem = !item.sourceItemId
        return create(QuoteLineItemInputSchema, {
          itemId: isCustomItem ? undefined : item.sourceItemId,
          quantity: item.quantity,
          unitPriceCents: BigInt(item.unitPriceCents),
          // Always send name, description, and unit so overrides are preserved for catalog items too
          name: item.name,
          description: item.description,
          unit: itemUnitEnumToString(item.unit),
          isTaxable: item.isTaxable,
          salesTaxRateId: item.salesTaxRateId,
          salesTaxRateName: item.salesTaxRateName,
          salesTaxRate: item.salesTaxRate,
          salesTaxCents: BigInt(item.salesTaxCents || 0),
        })
      })
    : []

  return create(UpdateInvoiceRequestSchema, {
    id: invoice.id,
    title: values.title,
    description: descriptionHtml || undefined,
    tipCents: BigInt(newTipCents),
    enforcedTipPercentage: values.enforcedTipPercentage,
    enforcedTipEnabled: (values.enforcedTipPercentage ?? 0) > 0,
    tipAllocationMode: nextTipAllocationMode,
    discountId: values.discountId || undefined,
    discountName: values.discountName || undefined,
    discountType: values.discountType || undefined,
    discountValue: values.discountValue || undefined,
    lineItems,
    revisionReason: lineItemsChanged ? (values.revisionReason || undefined) : undefined,
    updateMask: mask,
    checkoutPaymentMethods: nextCheckoutPaymentMethods,
    paymentScheduleAdjustmentStrategy,
  })
}


export interface InvoiceFormValues {
  title: string
  discountId?: string
  discountName?: string
  discountType?: DiscountTypeEnum
  discountValue?: number
  tipAmount: number // Stored in dollars for form, converted to cents in buildUpdateRequest
  enforcedTipPercentage?: number
  tipAllocationMode: TipAllocationModeEnum
  validUntil?: Date // Not used for invoices, but required for shared FinancialDocumentFormValues interface
  items: QuoteLineItem[]
  paymentSchedules: InvoicePaymentScheduleFormValue[]
  revisionReason: string
  useCustomPaymentMethods: boolean
  checkoutPaymentMethods: StripeCheckoutPaymentMethodEnum[]
}

/** Tip shortcut buttons shown when the org has not customized its tip selector. */
const FALLBACK_TIP_PERCENTAGES = [10, 15, 20]

/** Org tip percentages arrive as widened floats; see roundTipPercentage. */
export const normalizeTipPercentage = roundTipPercentage

/** Tip in cents for a percentage of the discounted subtotal; rounds like the portal. */
export function tipCentsForPercentage(subtotalAfterDiscountCents: number, percentage: number): number {
  if (subtotalAfterDiscountCents <= 0) return 0
  return Math.round(subtotalAfterDiscountCents * percentage / 100)
}

function TipAmountField({
  value,
  onChange,
  onPercentageSelect,
  percentages,
  selectedPercentage,
  defaultPercentage,
  subtotalCents,
  discountAmountCents,
  disableInputs,
}: {
  value: number
  /** Manual dollar entry. */
  onChange: (v: number) => void
  onPercentageSelect: (percentage: number) => void
  percentages: number[]
  selectedPercentage?: number
  defaultPercentage?: number
  subtotalCents: number
  discountAmountCents: number
  disableInputs: boolean
}) {
  const [tipDisplayValue, setTipDisplayValue] = React.useState(
    value ? value.toFixed(2) : ''
  )
  const [tipIsFocused, setTipIsFocused] = React.useState(false)

  React.useEffect(() => {
    if (!tipIsFocused) {
      setTipDisplayValue(value ? value.toFixed(2) : '')
    }
  }, [value, tipIsFocused])

  const subtotalAfterDiscount = (subtotalCents - discountAmountCents) / 100

  return (
    <div className="grid min-w-0 gap-3">
      <Label>Tip Amount</Label>
      <InputGroup>
        <InputGroupAddon align="inline-start">
          <InputGroupText>$</InputGroupText>
        </InputGroupAddon>
        <InputGroupInput
          type="number"
          min="0"
          max={subtotalAfterDiscount}
          step="0.01"
          value={tipDisplayValue}
          disabled={disableInputs}
          onChange={(event) => {
            setTipDisplayValue(event.target.value)
            onChange(parseFloat(event.target.value) || 0)
          }}
          onFocus={() => setTipIsFocused(true)}
          onBlur={() => {
            setTipIsFocused(false)
            const val = parseFloat(tipDisplayValue) || 0
            const clamped = Math.max(0, Math.min(val, subtotalAfterDiscount))
            setTipDisplayValue(clamped.toFixed(2))
            onChange(clamped)
          }}
        />
      </InputGroup>
      <div className="flex gap-2">
        {[0, ...percentages].map((percentage) => {
          const isSelected = selectedPercentage === percentage
          return (
            <Button
              key={percentage}
              type="button"
              variant={isSelected ? 'default' : 'outline'}
              size="sm"
              className="flex-1"
              aria-pressed={isSelected}
              title={percentage === defaultPercentage ? 'Default tip' : undefined}
              disabled={disableInputs}
              onClick={() => onPercentageSelect(percentage)}
            >
              {percentage}%
            </Button>
          )
        })}
      </div>
    </div>
  )
}

export function InvoiceEditor({
  invoice,
  organizationId,
  availableItems,
  initialIsEditing,
  onCancel,
  onSuccess,
}: InvoiceEditorProps) {
  const resolvedOrganizationId = organizationId || invoice.organizationId || ''
  const { items: catalogItems } = useListItems(resolvedOrganizationId)
  const { taxRates } = useListTaxRates(resolvedOrganizationId)
  const { discounts } = useListDiscounts(resolvedOrganizationId)
  const itemsSource: Item[] = availableItems ?? catalogItems
  const { events: bookingEvents } = useEventsList({
    bookingId: invoice.bookingId,
    enabled: !!invoice.bookingId,
  })

  const { mutation: createMutation } = useCreateInvoice()
  const { mutation: updateMutation } = useUpdateInvoice()

  const invoiceId = !invoice.id || invoice.id === 'new-invoice' ? '' : invoice.id
  const { replaceSchedules } = useReplacePaymentSchedules(invoiceId)
  const { updateSchedule } = useUpdatePaymentSchedule(invoiceId)

  const [isEditing, setIsEditing] = React.useState(initialIsEditing || false)
  const [paymentScheduleAdjustmentStrategy, setPaymentScheduleAdjustmentStrategy] =
    React.useState<InvoicePaymentScheduleAdjustmentStrategyEnum>(
      InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN,
    )

  // Payment dialog state
  const [showRecordPaymentDialog, setShowRecordPaymentDialog] = React.useState(false)
  const [showRefundDialog, setShowRefundDialog] = React.useState(false)
  const [showVoidDialog, setShowVoidDialog] = React.useState(false)
  const [showPaymentScheduleAdjustmentDialog, setShowPaymentScheduleAdjustmentDialog] = React.useState(false)
  const [initialScheduleIdForPayment, setInitialScheduleIdForPayment] = React.useState<string | undefined>(undefined)
  const [selectedStripePaymentForRefund, setSelectedStripePaymentForRefund] = React.useState<StripePayment | null>(null)
  const [selectedManualPaymentForVoid, setSelectedManualPaymentForVoid] = React.useState<ManualPayment | null>(null)

  const isNewInvoice = !invoice.id || invoice.id === 'new-invoice'
  const paymentScheduleSectionLabel = isNewInvoice ? 'Payment Schedule' : 'Payments'
  const scrollToPaymentSection = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      document.getElementById('invoice-payment-schedule-tab-content')?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      })
    })
  }, [])

  const focusSection = React.useCallback((section: InvoiceEditorSectionId) => {
    window.requestAnimationFrame(() => {
      const card = document.getElementById(INVOICE_EDITOR_SECTION_DOM_IDS[section])
      card?.focus()
      card?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      })
    })
  }, [])

  const syncPaymentSchedules = async (
    formSchedules: InvoicePaymentScheduleFormValue[],
    invoiceTotalCents: number,
  ) => {
    // Runs AFTER UpdateInvoice has landed the new total: the database enforces
    // sum(schedules) == invoice.total at commit, so the replace can only be
    // valid once the invoice carries the total the edited rows add up to.
    const plan = planInvoicePaymentScheduleSync(
      invoice.paymentSchedules || [],
      normalizeInvoicePaymentSchedules(formSchedules, invoiceTotalCents),
    )

    if (plan.kind === 'row-updates') {
      for (const update of plan.updates) {
        await updateSchedule(
          create(UpdatePaymentScheduleRequestSchema, {
            id: update.id,
            description: update.description || undefined,
            dueDate: dateToDateOnlyTimestamp(update.dueDate),
            updateMask: create(FieldMaskSchema, { paths: update.paths }),
          }),
        )
      }

      return
    }

    await replaceSchedules(
      create(ReplacePaymentSchedulesRequestSchema, {
        invoiceId: invoice.id,
        schedules: plan.schedules.map((s) =>
          create(PaymentScheduleInputSchema, {
            sequenceNumber: s.sequenceNumber,
            description: s.description || '',
            dueDate: dateToDateOnlyTimestamp(s.dueDate),
            amountCents: BigInt(s.amountCents),
          }),
        ),
      }),
    )
  }

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      Placeholder.configure({
        placeholder: 'Add invoice description...',
      }),
    ],
    content: invoice.description || '',
    editable: isEditing,
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[120px] px-3 py-2 prose prose-sm max-w-none',
      },
    },
  })

  React.useEffect(() => {
    editor?.setEditable(isEditing)
  }, [editor, isEditing])

  React.useEffect(() => {
    if (invoice.description && editor) {
      editor.commands.setContent(invoice.description)
    }
  }, [invoice.description, editor])

  const initialItems = React.useMemo<QuoteLineItem[]>(() => {
    // Existing invoice: load from invoice.lineItems
    if (invoice.lineItems && invoice.lineItems.length > 0) {
      return invoice.lineItems.map((item) => ({
        id: item.id,
        sourceItemId: item.sourceItemId,
        name: item.name,
        description: item.description || '',
        quantity: item.quantity,
        unitPriceCents: Number(item.unitPriceCents),
        unit: item.unit ? stringToItemUnitEnum(item.unit) : ItemUnitEnum.ITEM_UNIT_PER_EVENT,
        sortOrder: item.sortOrder,
        lineTotalCents: item.lineTotalCents ? Number(item.lineTotalCents) : undefined,
        // Per-line-item tax fields
        isTaxable: item.isTaxable ?? true,
        salesTaxRateId: item.salesTaxRateId ?? undefined,
        salesTaxRateName: item.salesTaxRateName ?? undefined,
        salesTaxRate: item.salesTaxRate ?? undefined,
        salesTaxCents: item.salesTaxCents ? Number(item.salesTaxCents) : 0,
      }))
    }

    // NEW INVOICE: Pre-populate with "always include" items from organization
    if (itemsSource.length > 0) {
      const alwaysIncludeItems = itemsSource
        .filter(item => item.alwaysInclude && item.unit !== ItemUnitEnum.ITEM_UNIT_PER_MILE)
        .map((item, index) => ({
          tempId: crypto.randomUUID(),
          sourceItemId: item.id,
          name: item.title,
          description: item.description || '',
          quantity: 1,
          unitPriceCents: dollarsToCents(item.price),
          unit: item.unit || ItemUnitEnum.ITEM_UNIT_PER_EVENT,
          sortOrder: index,
          isTaxable: true,
          salesTaxCents: 0,
        } as QuoteLineItem))

      return alwaysIncludeItems
    }

    return []
  }, [invoice.lineItems, itemsSource])

  // Latest enforced minimum tip, read at submit time (the submit handler closes over
  // the first render, so it cannot read the totals computed further down directly).
  const enforcedTipCentsRef = React.useRef(0)

  // A saved invoice shows its own mode; a new one starts from the org default.
  const orgTipAllocationMode = useOrgTipAllocationModeDefault()
  const initialTipAllocationMode =
    !invoice.id || invoice.id === 'new-invoice'
      ? orgTipAllocationMode
      : normalizeTipAllocationMode(invoice.tipAllocationMode)

  const form = useForm({
    defaultValues: {
      title: invoice.title || '',
      discountId: invoice.discountId || undefined,
      discountName: invoice.discountName || undefined,
      discountType: sanitizeDiscountType(invoice.discountType),
      discountValue: invoice.discountValue || undefined,
      tipAmount: Number(invoice.tipCents || 0n) / 100, // Convert from cents to dollars
      enforcedTipPercentage: invoice.enforcedTipPercentage || 0,
      tipAllocationMode: initialTipAllocationMode,
      validUntil: undefined, // Not used for invoices
      items: initialItems.map(item => ({
        ...item,
        isTaxable: item.isTaxable ?? true,
        salesTaxRateId: item.salesTaxRateId ?? undefined,
        salesTaxRateName: item.salesTaxRateName ?? undefined,
        salesTaxRate: item.salesTaxRate ?? undefined,
        salesTaxCents: item.salesTaxCents ?? 0,
      })),
      paymentSchedules: invoice.paymentSchedules
        ? invoicePaymentSchedulesToFormValues(invoice.paymentSchedules)
        : [],
      revisionReason: '',
      useCustomPaymentMethods: (invoice.checkoutPaymentMethods?.length ?? 0) > 0,
      checkoutPaymentMethods: invoice.checkoutPaymentMethods?.length
        ? [...invoice.checkoutPaymentMethods]
        : [...DEFAULT_CHECKOUT_PAYMENT_METHODS],
    },
    onSubmit: async ({ value }) => {
      const paymentSchedulesForSubmit = normalizeInvoicePaymentSchedules(value.paymentSchedules, totalCents)
      const existingPaymentSchedules = invoice.paymentSchedules || []
      const paymentSchedulesChanged = !isNewInvoice &&
        haveInvoicePaymentSchedulesChanged(existingPaymentSchedules, value.paymentSchedules)
      const currentInvoiceTotalCents = Number(invoice.totalCents || 0n)
      const invoiceTotalChanged = !isNewInvoice && totalCents !== currentInvoiceTotalCents
      let effectivePaymentScheduleAdjustmentStrategy = paymentSchedulesChanged
        ? inferPaymentScheduleAdjustmentStrategy(
            existingPaymentSchedules,
            value.paymentSchedules,
            totalCents,
            paymentScheduleAdjustmentStrategy,
          )
        : paymentScheduleAdjustmentStrategy
      const paymentSchedulesChangedByUser = getPaymentSchedulesChangedByUser(
        paymentSchedulesChanged,
        existingPaymentSchedules,
        value.paymentSchedules,
        totalCents,
        effectivePaymentScheduleAdjustmentStrategy,
      )
      const activeCheckoutSessionCount = existingPaymentSchedules.filter(hasActiveCheckoutSession).length
      const paymentScheduleTotalMismatch = getPaymentScheduleTotalMismatch(paymentSchedulesForSubmit, totalCents)

      if (
        invoiceTotalChanged &&
        paymentSchedulesChangedByUser &&
        effectivePaymentScheduleAdjustmentStrategy === InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN
      ) {
        effectivePaymentScheduleAdjustmentStrategy =
          InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_LAST_PAYMENT
      }

      // Validate payment schedules before submission
      if (paymentScheduleTotalMismatch) {
        // Line items changed the invoice total but the schedule wasn't touched -
        // let the user resolve the mismatch via the adjustment dialog instead of
        // dead-ending on a toast. Only offer the dialog if a strategy hasn't
        // already been picked this session - otherwise a strategy that turned
        // out to be a no-op (e.g. the reduction floor blocked it) would reopen
        // the identical dialog forever instead of surfacing an error.
        const paymentScheduleAdjustmentAlreadyAttempted =
          paymentScheduleAdjustmentStrategy !== InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN
        if (!isNewInvoice && !paymentSchedulesChangedByUser && !paymentScheduleAdjustmentAlreadyAttempted) {
          setShowPaymentScheduleAdjustmentDialog(true)
          return
        }

        toast.error(isNewInvoice ? 'Invalid payment schedule' : 'Choose a payment schedule update', {
          description: isNewInvoice
            ? paymentScheduleTotalMismatch.message
            : 'Open Payments and choose how to apply the invoice total change before saving.',
        })
        return
      }

      const paidInvoiceScheduleChangeError = !isNewInvoice && paymentSchedulesChangedByUser &&
        existingPaymentSchedules.some(isPaymentSchedulePaid)
        ? getPaidInvoiceScheduleManualChangeError(existingPaymentSchedules, value.paymentSchedules)
        : undefined
      if (paidInvoiceScheduleChangeError) {
        toast.error('Payment schedule has locked items', {
          description: paidInvoiceScheduleChangeError,
        })
        return
      }

      if (!isNewInvoice && activeCheckoutSessionCount > 0 && paymentSchedulesChangedByUser) {
        toast.error('Payment schedule is locked', {
          description: 'A customer has an active checkout link. Wait for it to expire before changing the payment schedule.',
        })
        return
      }

      if (!isNewInvoice && activeCheckoutSessionCount > 0 && totalCents < currentInvoiceTotalCents) {
        toast.error('Cannot reduce invoice while payment link is active', {
          description: 'A customer has an active checkout link for this invoice. Wait for it to expire before reducing the total.',
        })
        return
      }

      const htmlContent = editor?.getHTML() || invoice.description || ''

      if (isNewInvoice) {
        // CREATE NEW INVOICE
        try {
          // Convert line items to proto format
          const lineItems = value.items.map((item) => {
            const isCustomItem = !item.sourceItemId

            return create(QuoteLineItemInputSchema, {
              itemId: isCustomItem ? undefined : item.sourceItemId,
              quantity: item.quantity,
              unitPriceCents: BigInt(item.unitPriceCents),
              // Always send name, description, and unit so overrides are preserved for catalog items too
              name: item.name,
              description: item.description,
              unit: itemUnitEnumToString(item.unit),
              isTaxable: item.isTaxable,
              salesTaxRateId: item.salesTaxRateId,
              salesTaxRateName: item.salesTaxRateName,
              salesTaxRate: item.salesTaxRate,
              salesTaxCents: BigInt(item.salesTaxCents || 0),
            })
          })

          // Convert payment schedules to proto format
          const paymentSchedules = formValuesToInvoicePaymentSchedules(paymentSchedulesForSubmit)

          // Create the invoice request
          // Always explicit, so a deliberate $0 tip is not replaced by the org default
          // server-side. Never below the enforced minimum: save the tip the totals show.
          const tipCentsValue = BigInt(Math.max(0, Math.round(value.tipAmount * 100), enforcedTipCentsRef.current))
          const request = create(CreateInvoiceRequestSchema, {
            organizationId: resolvedOrganizationId,
            bookingId: invoice.bookingId,
            contactId: invoice.contactId,
            title: value.title || undefined,
            description: htmlContent || undefined,
            issueDate: dateToTimestamp(new Date()), // Default to current date
            lineItems: lineItems,
            paymentSchedules: paymentSchedules,
            discountId: value.discountId || undefined,
            discountName: value.discountName || undefined,
            discountType: value.discountType || undefined,
            discountValue: value.discountValue || undefined,
            enforcedTipEnabled: value.enforcedTipPercentage ? true : false,
            enforcedTipPercentage: value.enforcedTipPercentage,
            tipAllocationMode: normalizeTipAllocationMode(value.tipAllocationMode),
            tipCents: tipCentsValue,
            checkoutPaymentMethods: value.useCustomPaymentMethods ? value.checkoutPaymentMethods : [],
          })

          const response = await createMutation.mutateAsync(request)
          toast.success('Invoice created successfully')
          onSuccess?.(response.invoice?.id)
        } catch (error) {
          console.error('Failed to create invoice:', error)
          const copy = getErrorMessage(error)
          reportError(error, { feature: 'invoice_create', kind: 'mutation' })
          if (!copy.expected) {
            captureError(error instanceof Error ? error : new Error(String(error)), { context: 'invoice-editor-create' })
          }
          toast.error(copy.expected ? copy.title : 'Failed to create invoice', {
            description: copy.expected ? copy.description : getErrorDescription(error),
          })
        }
      } else {
        // UPDATE EXISTING INVOICE
        try {
          const request = buildUpdateRequest(invoice, value, htmlContent, effectivePaymentScheduleAdjustmentStrategy)
          await updateMutation.mutateAsync(request)
          if (paymentSchedulesChangedByUser) {
            try {
              await syncPaymentSchedules(value.paymentSchedules, totalCents)
            } catch (scheduleError) {
              console.error('Failed to sync payment schedules:', scheduleError)
              const copy = getErrorMessage(scheduleError)
              reportError(scheduleError, { feature: 'invoice_sync_payment_schedules', kind: 'mutation' })
              if (!copy.expected) {
                captureError(scheduleError instanceof Error ? scheduleError : new Error(String(scheduleError)), { context: 'invoice-editor-sync-schedules' })
              }
              toast.error(copy.expected ? copy.title : 'Invoice saved, but failed to update payment schedules. Please try again.', {
                description: copy.expected ? copy.description : getErrorDescription(scheduleError),
              })
              setIsEditing(false)
              onSuccess?.()
              return
            }
          }
          toast.success('Invoice updated successfully')
          setPaymentScheduleAdjustmentStrategy(
            InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN,
          )
          setIsEditing(false)
          onSuccess?.()
        } catch (error) {
          console.error('Failed to update invoice:', error)
          const copy = getErrorMessage(error)
          reportError(error, { feature: 'invoice_update', kind: 'mutation' })
          if (!copy.expected) {
            captureError(error instanceof Error ? error : new Error(String(error)), { context: 'invoice-editor-update' })
          }
          toast.error(copy.expected ? copy.title : 'Failed to update invoice', {
            description: copy.expected ? copy.description : getErrorDescription(error),
          })
        }
      }
    },
  })

  const watchedFormValues = useStore(form.store, (state) => ({
    discountId: state.values.discountId,
    discountType: state.values.discountType,
    discountValue: state.values.discountValue,
    items: state.values.items,
    enforcedTipPercentage: state.values.enforcedTipPercentage,
    tipAmount: state.values.tipAmount,
    paymentSchedules: state.values.paymentSchedules,
  }))

  // Use the centralized totals calculation hook
  const {
    subtotalCents,
    discountAmountCents,
    discountRatio,
    taxAmountCents,
    enforcedTipCents,
    tipCents,
    totalCents,
  } = useFinancialDocumentTotals({
    items: watchedFormValues.items,
    discountType: watchedFormValues.discountType,
    discountValue: watchedFormValues.discountValue,
    enforcedTipPercentage: watchedFormValues.enforcedTipPercentage,
    manualTipCents: Math.round(watchedFormValues.tipAmount * 100),
    existingTotals: !isEditing && invoice.subtotalCents
      ? {
        subtotalCents: Number(invoice.subtotalCents),
        discountCents: Number(invoice.discountCents || 0),
        salesTaxCents: Number(invoice.salesTaxCents || 0),
        enforcedTipCents: Number(invoice.enforcedTipCents || 0),
        totalCents: Number(invoice.totalCents),
      }
      : undefined,
    isReadOnly: !isEditing,
  })

  enforcedTipCentsRef.current = enforcedTipCents

  // Tip shortcuts follow the org's tip selector. A new invoice starts on the org's
  // default tip percentage and keeps tracking it as line items change, until the
  // user picks another percentage or types an amount.
  const { currentOrganization } = useCurrentOrganization()
  const tipSelectorSettings = currentOrganization?.tipSelectorSettings
  const tipPercentages = tipSelectorSettings
    ? tipSelectorSettings.customPercentages.map(normalizeTipPercentage)
    : FALLBACK_TIP_PERCENTAGES
  const defaultTipPercentage = tipSelectorSettings?.defaultPercentage === undefined
    ? undefined
    : normalizeTipPercentage(tipSelectorSettings.defaultPercentage)
  const [tipPercentage, setTipPercentage] = React.useState<number | undefined>(undefined)
  const hasAppliedDefaultTipRef = React.useRef(false)
  const subtotalAfterDiscountCents = subtotalCents - discountAmountCents

  React.useEffect(() => {
    if (!isNewInvoice || hasAppliedDefaultTipRef.current || !currentOrganization) return
    hasAppliedDefaultTipRef.current = true
    if (defaultTipPercentage !== undefined && form.getFieldValue('tipAmount') === 0) {
      setTipPercentage(defaultTipPercentage)
    }
  }, [isNewInvoice, currentOrganization, defaultTipPercentage, form])

  // The org may finish loading after mount; a new invoice follows its default until the user picks one.
  React.useEffect(() => {
    if (!isNewInvoice) return
    if (form.getFieldMeta('tipAllocationMode')?.isTouched) return
    if (form.getFieldValue('tipAllocationMode') !== orgTipAllocationMode) {
      form.setFieldValue('tipAllocationMode', orgTipAllocationMode)
    }
  }, [isNewInvoice, orgTipAllocationMode, form])

  React.useEffect(() => {
    if (!isNewInvoice || tipPercentage === undefined) return
    const nextTipCents = tipCentsForPercentage(subtotalAfterDiscountCents, tipPercentage)
    if (Math.round(form.getFieldValue('tipAmount') * 100) !== nextTipCents) {
      form.setFieldValue('tipAmount', nextTipCents / 100)
    }
  }, [isNewInvoice, tipPercentage, subtotalAfterDiscountCents, form])

  const handleTipPercentageSelect = React.useCallback((percentage: number) => {
    // Existing invoices take a one-off amount; new invoices keep following the percentage.
    hasAppliedDefaultTipRef.current = true
    setTipPercentage(isNewInvoice ? percentage : undefined)
    form.setFieldValue('tipAmount', tipCentsForPercentage(subtotalAfterDiscountCents, percentage) / 100)
  }, [isNewInvoice, subtotalAfterDiscountCents, form])

  const disableInputs = !isEditing

  // Split tips need the v1 tip ledger, and the server refuses a tip change while a
  // payment is processing, so say why up front instead of failing on save.
  const tipAllocationDisabledReason = !isNewInvoice && invoice.tipAccountingVersion !== 1
    ? 'Not available on invoices created before split-tip support'
    : getInFlightPaymentSchedules(invoice.paymentSchedules).length > 0
      ? 'A payment is processing. You can change this after it clears.'
      : undefined

  // Payments are now accessed via payment schedules and payment intents
  // For display purposes, we'll show payment schedules
  const paymentSchedules = invoice.paymentSchedules || []
  const hasCheckoutSessionSnapshots = paymentSchedules.some((schedule) => Boolean(schedule.activeCheckoutSession))
  const [nowMs, setNowMs] = React.useState(() => Date.now())

  React.useEffect(() => {
    if (!hasCheckoutSessionSnapshots) return

    const initialNowMs = Date.now()
    setNowMs(initialNowMs)
    if (!paymentSchedules.some((schedule) => hasActiveCheckoutSession(schedule, initialNowMs))) return

    const interval = window.setInterval(() => {
      const nextNowMs = Date.now()
      setNowMs(nextNowMs)
      if (!paymentSchedules.some((schedule) => hasActiveCheckoutSession(schedule, nextNowMs))) {
        window.clearInterval(interval)
      }
    }, 1000)
    return () => window.clearInterval(interval)
  }, [hasCheckoutSessionSnapshots, paymentSchedules])

  const activeCheckoutSessionCount = paymentSchedules.filter((schedule) => hasActiveCheckoutSession(schedule, nowMs)).length
  const scheduleEditorDisabled = disableInputs
  const lockedScheduleReasons = React.useMemo(() => {
    const reasons: Record<string, string> = {}
    for (const schedule of paymentSchedules) {
      const reason = getInvoicePaymentScheduleLockReason(schedule, nowMs)
      if (reason) {
        reasons[schedule.id] = reason
      }
    }
    return reasons
  }, [paymentSchedules, nowMs])

  const handlePaymentSchedulesChange = React.useCallback((schedules: InvoicePaymentScheduleFormValue[]) => {
    setPaymentScheduleAdjustmentStrategy(
      InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN,
    )
    form.setFieldValue('paymentSchedules', schedules)
  }, [form])

  const applyPaymentScheduleAdjustmentStrategy = React.useCallback((
    strategy: InvoicePaymentScheduleAdjustmentStrategyEnum,
  ) => {
    const nextSchedules = strategy === InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_NEW_PAYMENT
      ? appendInvoicePaymentScheduleForTotal(watchedFormValues.paymentSchedules, totalCents)
      : rebalanceInvoicePaymentSchedulesForTotal(paymentSchedules, watchedFormValues.paymentSchedules, totalCents)
    form.setFieldValue('paymentSchedules', nextSchedules)
    setPaymentScheduleAdjustmentStrategy(strategy)
  }, [form, watchedFormValues.paymentSchedules, paymentSchedules, totalCents])

  const handleApplyBalanceToLastPayment = React.useCallback(() => {
    applyPaymentScheduleAdjustmentStrategy(
      InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_LAST_PAYMENT,
    )
  }, [applyPaymentScheduleAdjustmentStrategy])

  const handleAddBalanceAsNewPayment = React.useCallback(() => {
    applyPaymentScheduleAdjustmentStrategy(
      InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_NEW_PAYMENT,
    )
  }, [applyPaymentScheduleAdjustmentStrategy])

  const handlePaymentScheduleAdjustmentDialogSelect = React.useCallback((
    strategy: InvoicePaymentScheduleAdjustmentStrategyEnum,
  ) => {
    applyPaymentScheduleAdjustmentStrategy(strategy)
    setShowPaymentScheduleAdjustmentDialog(false)
    form.handleSubmit()
  }, [applyPaymentScheduleAdjustmentStrategy, form])

  // Fetch payment data for the overview card
  const { stripePayments: _stripePayments, manualPayments: _manualPayments, totalCount: paymentCount } = useInvoicePayments(
    isNewInvoice ? '' : invoice.id
  )

  const railActions = (
    <>
      {isNewInvoice && (
        <form.Subscribe selector={(s) => [s.values.title, s.values.items, s.values.paymentSchedules, s.isSubmitting] as const}>
          {([title, items, paymentSchedules, isSubmitting]) => {
            const paymentScheduleTotalMismatch = getPaymentScheduleTotalMismatch(paymentSchedules, totalCents)

            return (
              <Button
                type="submit"
                className="w-full"
                disabled={!title.trim() || items.length === 0 || !!paymentScheduleTotalMismatch || isSubmitting || createMutation.isPending}
              >
                {createMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                {createMutation.isPending ? 'Creating…' : 'Create Invoice'}
              </Button>
            )
          }}
        </form.Subscribe>
      )}
      {!isNewInvoice && !isEditing && (
        <Button
          type="button"
          className="w-full"
          onClick={() => {
            setPaymentScheduleAdjustmentStrategy(
              InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN,
            )
            setIsEditing(true)
          }}
        >
          Edit Invoice
        </Button>
      )}
      {!isNewInvoice && isEditing && (
        <form.Subscribe selector={(s) => [s.values.title, s.values.items, s.values.paymentSchedules, s.isSubmitting] as const}>
          {([title, items, formPaymentSchedules, isSubmitting]) => {
            const paymentScheduleTotalMismatch = getPaymentScheduleTotalMismatch(formPaymentSchedules, totalCents)
            const existingPaymentSchedules = invoice.paymentSchedules || []
            const paymentSchedulesChanged = haveInvoicePaymentSchedulesChanged(existingPaymentSchedules, formPaymentSchedules)
            const effectivePaymentScheduleAdjustmentStrategy = paymentSchedulesChanged
              ? inferPaymentScheduleAdjustmentStrategy(
                  existingPaymentSchedules,
                  formPaymentSchedules,
                  totalCents,
                  paymentScheduleAdjustmentStrategy,
                )
              : paymentScheduleAdjustmentStrategy
            const paymentSchedulesChangedByUser = getPaymentSchedulesChangedByUser(
              paymentSchedulesChanged,
              existingPaymentSchedules,
              formPaymentSchedules,
              totalCents,
              effectivePaymentScheduleAdjustmentStrategy,
            )
            // Only block Save on a schedule mismatch caused by the user's own
            // manual schedule edits - a mismatch from a pure total change is
            // resolved via the adjustment dialog when Save is clicked.
            const blockingMismatch = !!paymentScheduleTotalMismatch && paymentSchedulesChangedByUser

            return (
              <Button
                type="submit"
                className="w-full"
                disabled={!title.trim() || items.length === 0 || blockingMismatch || isSubmitting || updateMutation.isPending}
              >
                {updateMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            )
          }}
        </form.Subscribe>
      )}
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => {
          if (isEditing) {
            setIsEditing(false)
            setPaymentScheduleAdjustmentStrategy(
              InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_UNKNOWN,
            )
            form.reset({
              title: invoice.title || '',
              discountId: invoice.discountId || undefined,
              discountName: invoice.discountName || undefined,
              discountType: sanitizeDiscountType(invoice.discountType),
              discountValue: invoice.discountValue || undefined,
              tipAmount: Number(invoice.tipCents || 0n) / 100,
              enforcedTipPercentage: invoice.enforcedTipPercentage || 0,
              tipAllocationMode: initialTipAllocationMode,
              validUntil: undefined, // Not used for invoices
              items: initialItems.map(item => ({
                ...item,
                salesTaxRateId: item.salesTaxRateId ?? undefined,
                salesTaxRateName: item.salesTaxRateName ?? undefined,
                salesTaxRate: item.salesTaxRate ?? undefined,
              })),
              paymentSchedules: invoice.paymentSchedules
                ? invoicePaymentSchedulesToFormValues(invoice.paymentSchedules)
                : [],
              revisionReason: '',
              useCustomPaymentMethods: (invoice.checkoutPaymentMethods?.length ?? 0) > 0,
              checkoutPaymentMethods: invoice.checkoutPaymentMethods?.length
                ? [...invoice.checkoutPaymentMethods]
                : [...DEFAULT_CHECKOUT_PAYMENT_METHODS],
            })
            editor?.commands.setContent(invoice.description || '')
          }
          onCancel?.()
        }}
      >
        {isEditing ? 'Cancel' : 'Back'}
      </Button>
    </>
  )

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        form.handleSubmit()
      }}
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,27rem)] xl:items-start">
        <div className="min-w-0 space-y-4">
          <section
            id={INVOICE_EDITOR_SECTION_DOM_IDS.details}
            aria-labelledby="invoice-details-heading"
            className="space-y-5 rounded-2xl border bg-card p-5"
          >
            <h2 id="invoice-details-heading" className="text-base font-semibold">Invoice Details</h2>

            <FinancialDocumentDetailsFields
              form={form}
              richTextEditor={
                <RichTextEditor
                  editor={editor}
                  disabled={disableInputs}
                  className="min-h-[150px]"
                />
              }
              discounts={discounts}
              organizationId={resolvedOrganizationId}
              disabled={disableInputs}
              showValidUntil={false}
              supportsInlineDiscounts={true}
              tipAllocationControl={
                <form.Field name="tipAllocationMode">
                  {(field) => (
                    <TipAllocationModeSelect
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      disabled={disableInputs || tipAllocationDisabledReason !== undefined}
                      disabledReason={tipAllocationDisabledReason}
                    />
                  )}
                </form.Field>
              }
            />

            {/* Payment Methods Override */}
            <div className="space-y-3 border-t pt-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold">Payment Methods</h3>
                <p className="text-xs text-muted-foreground">
                  Override which payment methods are available at checkout
                </p>
              </div>

              <form.Subscribe selector={(state) => ({
                useCustom: state.values.useCustomPaymentMethods,
                methods: state.values.checkoutPaymentMethods,
              })}>
                {({ useCustom, methods }) => {
                  const hasCard = methods.includes(StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_CARD)
                  const hasACH = methods.includes(StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_US_BANK_ACCOUNT)

                  const toggleMethod = (method: StripeCheckoutPaymentMethodEnum, checked: boolean) => {
                    const current = form.state.values.checkoutPaymentMethods
                    let next: StripeCheckoutPaymentMethodEnum[]
                    if (checked) {
                      next = [...current, method]
                    } else {
                      next = current.filter(m => m !== method)
                    }
                    // Ensure at least one method is selected
                    if (next.length === 0) return
                    form.setFieldValue('checkoutPaymentMethods', next)
                  }

                  return (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <form.Field name="useCustomPaymentMethods">
                            {(field) => (
                              <Switch
                                id="invoice-custom-payment-methods"
                                checked={field.state.value}
                                onCheckedChange={(checked) => {
                                  field.handleChange(checked)
                                  if (!checked) {
                                    // Reset to both methods when switching back to org defaults
                                    form.setFieldValue('checkoutPaymentMethods', [...DEFAULT_CHECKOUT_PAYMENT_METHODS])
                                  }
                                }}
                                disabled={disableInputs}
                              />
                            )}
                          </form.Field>
                          <Label htmlFor="invoice-custom-payment-methods" className="text-sm font-medium">
                            Customize checkout methods
                          </Label>
                        </div>
                        {!useCustom && (
                          <p className="text-sm text-muted-foreground">
                            Using organization default payment methods
                          </p>
                        )}
                      </div>

                      {useCustom && (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <Label htmlFor="payment-method-card" className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-normal">
                            <Checkbox
                              id="payment-method-card"
                              checked={hasCard}
                              onCheckedChange={(checked) =>
                                toggleMethod(StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_CARD, !!checked)
                              }
                              disabled={disableInputs || (hasCard && !hasACH)}
                            />
                            Credit Card
                          </Label>
                          <Label htmlFor="payment-method-ach" className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-normal">
                            <Checkbox
                              id="payment-method-ach"
                              checked={hasACH}
                              onCheckedChange={(checked) =>
                                toggleMethod(StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_US_BANK_ACCOUNT, !!checked)
                              }
                              disabled={disableInputs || (hasACH && !hasCard)}
                            />
                            ACH / Bank Transfer
                          </Label>
                        </div>
                      )}
                    </>
                  )
                }}
              </form.Subscribe>
            </div>
          </section>

          <form.Field name="items">
            {(field) => (
              <section
                id={INVOICE_EDITOR_SECTION_DOM_IDS.items}
                aria-labelledby="invoice-line-items-heading"
                className="space-y-5 rounded-2xl border bg-card p-5"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 id="invoice-line-items-heading" className="text-base font-semibold">Line Items</h2>
                  <p className="text-xs text-muted-foreground">Drag to reorder · per-item tax and unit</p>
                </div>

                <QuoteLineItemsTable
                  items={withoutTransportationLineItems(field.state.value)}
                  useStoredTotals={!isEditing}
                  availableItems={itemsSource}
                  availableTaxRates={taxRates}
                  presentation="rows"
                  onItemsChange={(items) => {
                    field.handleChange(replacePrimaryLineItems(field.state.value, items).map(item => ({
                      ...item,
                      salesTaxRateId: item.salesTaxRateId ?? undefined,
                      salesTaxRateName: item.salesTaxRateName ?? undefined,
                      salesTaxRate: item.salesTaxRate ?? undefined,
                    })))
                  }}
                  disabled={!isEditing}
                  discountRatio={discountRatio}
                  discountAmountCents={discountAmountCents}
                />

                <div className="space-y-3 border-t pt-5">
                  <h3 className="text-sm font-semibold">Transportation</h3>
                  <TransportationLineItemSection
                    embedded
                    documentKey={invoice.id || `new-invoice-${invoice.bookingId || resolvedOrganizationId}`}
                    organizationId={resolvedOrganizationId}
                    events={bookingEvents.map((event, index) => ({
                      key: event.id,
                      label: `Event ${index + 1}`,
                      location: event.eventLocation,
                    }))}
                    items={field.state.value}
                    taxRates={taxRates}
                    disabled={!isEditing}
                    applyOrganizationDefault={isNewInvoice}
                    onItemsChange={(items) => field.handleChange(items.map((item) => ({
                      ...item,
                      salesTaxRateId: item.salesTaxRateId ?? undefined,
                      salesTaxRateName: item.salesTaxRateName ?? undefined,
                      salesTaxRate: item.salesTaxRate ?? undefined,
                      salesTaxCents: item.salesTaxCents ?? 0,
                    })))}
                  />
                </div>

                <div className="grid min-w-0 gap-4 border-t pt-5 sm:grid-cols-2 [&>*]:min-w-0">
                  <QuickTaxSelector
                    items={field.state.value}
                    taxRates={taxRates}
                    organizationId={resolvedOrganizationId}
                    onItemsChange={(items) => {
                      field.handleChange(items.map(item => ({
                        ...item,
                        salesTaxRateId: item.salesTaxRateId ?? undefined,
                        salesTaxRateName: item.salesTaxRateName ?? undefined,
                        salesTaxRate: item.salesTaxRate ?? undefined,
                      })))
                    }}
                    disabled={disableInputs}
                  />

                  {/* Tip Amount */}
                  <form.Field name="tipAmount">
                    {(field) => (
                      <TipAmountField
                        value={field.state.value}
                        onChange={(v) => {
                          hasAppliedDefaultTipRef.current = true
                          setTipPercentage(undefined)
                          field.handleChange(v)
                        }}
                        onPercentageSelect={handleTipPercentageSelect}
                        percentages={tipPercentages}
                        selectedPercentage={tipPercentage}
                        defaultPercentage={defaultTipPercentage}
                        subtotalCents={subtotalCents}
                        discountAmountCents={discountAmountCents}
                        disableInputs={disableInputs}
                      />
                    )}
                  </form.Field>
                </div>
              </section>
            )}
          </form.Field>

          {/* New invoices: standalone schedule editor. Existing invoices: schedule editor + payment overview + history */}
          <div id={INVOICE_EDITOR_SECTION_DOM_IDS.payment} className="space-y-6">
            {isNewInvoice ? (
              <section
                aria-labelledby="invoice-new-payment-schedule-heading"
                className="space-y-4 rounded-2xl border bg-card p-5"
              >
                <h2 id="invoice-new-payment-schedule-heading" className="text-base font-semibold">
                  Payment Schedule
                </h2>
                <form.Subscribe selector={(state) => ({ paymentSchedules: state.values.paymentSchedules })}>
                  {({ paymentSchedules: formPaymentSchedules }) => (
                    <InvoicePaymentScheduleEditor
                      schedules={formPaymentSchedules}
                      invoiceTotalCents={totalCents}
                      onSchedulesChange={(schedules) => form.setFieldValue('paymentSchedules', schedules)}
                      disabled={disableInputs}
                    />
                  )}
                </form.Subscribe>
              </section>
            ) : (
              <>
                {activeCheckoutSessionCount > 0 && (
                  <div role="alert" className="flex items-start gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg p-3 text-sm text-status-warning-fg">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    <div>
                      <p className="font-medium">Payment schedule locked while checkout is active</p>
                      <p className="mt-0.5">
                        {activeCheckoutSessionCount === 1
                          ? 'One payment link is active. Wait for it to expire before changing payment schedules or reducing the invoice total.'
                          : `${activeCheckoutSessionCount} payment links are active. Wait for them to expire before changing payment schedules or reducing the invoice total.`}
                      </p>
                    </div>
                  </div>
                )}
                <section
                  aria-labelledby="invoice-payment-schedule-heading"
                  className="space-y-4 rounded-2xl border bg-card p-5"
                >
                  <h2 id="invoice-payment-schedule-heading" className="text-base font-semibold">
                    Payment Schedule
                  </h2>
                  <form.Subscribe selector={(state) => ({ paymentSchedules: state.values.paymentSchedules })}>
                    {({ paymentSchedules: formPaymentSchedules }) => (
                      <InvoicePaymentScheduleEditor
                        schedules={formPaymentSchedules}
                        invoiceTotalCents={totalCents}
                        onSchedulesChange={handlePaymentSchedulesChange}
                        disabled={scheduleEditorDisabled}
                        lockedScheduleReasons={lockedScheduleReasons}
                        onApplyBalanceToLastPayment={handleApplyBalanceToLastPayment}
                        onAddBalanceAsNewPayment={handleAddBalanceAsNewPayment}
                      />
                    )}
                  </form.Subscribe>
                </section>

                <PaymentOverviewCard
                  invoice={invoice}
                  paymentCount={paymentCount}
                  onRecordPayment={() => {
                    const unpaidSchedule = paymentSchedules.find(
                      (s) => Number(s.amountPaidCents || 0) < Number(s.amountCents || 0)
                    )
                    setInitialScheduleIdForPayment(unpaidSchedule?.id)
                    setShowRecordPaymentDialog(true)
                  }}
                />

                <section
                  aria-labelledby="invoice-payment-history-heading"
                  className="space-y-4 rounded-2xl border bg-card p-5"
                >
                  <h2 id="invoice-payment-history-heading" className="text-base font-semibold">Payment History</h2>
                  <PaymentHistory
                    invoiceId={invoice.id}
                    organizationId={resolvedOrganizationId}
                    isDraft={invoice.status === InvoiceStatusEnum.INVOICE_STATUS_DRAFT}
                    onRecordPayment={() => {
                      const unpaidSchedule = paymentSchedules.find(
                        (s) => Number(s.amountPaidCents || 0) < Number(s.amountCents || 0)
                      )
                      setInitialScheduleIdForPayment(unpaidSchedule?.id)
                      setShowRecordPaymentDialog(true)
                    }}
                    onRefund={(payment) => {
                      setSelectedStripePaymentForRefund(payment)
                      setShowRefundDialog(true)
                    }}
                    onVoid={(payment) => {
                      setSelectedManualPaymentForVoid(payment)
                      setShowVoidDialog(true)
                    }}
                  />
                </section>
              </>
            )}
          </div>

          <section
            aria-labelledby="invoice-totals-heading"
            className="space-y-4 rounded-2xl border bg-card p-5"
          >
            <h2 id="invoice-totals-heading" className="text-base font-semibold">Totals &amp; Payment Info</h2>
            <form.Subscribe selector={(state) => ({
              items: state.values.items,
              discountId: state.values.discountId,
              tipAmount: state.values.tipAmount,
              enforcedTipPercentage: state.values.enforcedTipPercentage,
              paymentSchedules: state.values.paymentSchedules,
            })}>
              {({ items, enforcedTipPercentage, paymentSchedules: formPaymentSchedules }) => (
                <FinancialDocumentTotalsBreakdown
                  items={items}
                  useStoredTotals={!isEditing}
                  subtotalCents={subtotalCents}
                  discountAmountCents={discountAmountCents}
                  discountRatio={discountRatio}
                  taxAmountCents={taxAmountCents}
                  tipCents={tipCents}
                  enforcedTipPercentage={enforcedTipPercentage}
                  totalCents={totalCents}
                  paymentSchedules={formPaymentSchedules.map(schedule => ({
                    description: schedule.description,
                    dueDate: schedule.dueDate,
                    amountCents: schedule.amountCents || 0,
                  }))}
                  showPaymentInfo={true}
                  paymentInfo={{
                    amountPaidCents: invoice.amountPaidCents || 0n,
                    // During creation (no ID), show the invoice total as amount due
                    // For existing invoices, use the stored amountDueCents
                    amountDueCents: invoice.id && invoice.id !== 'new-invoice'
                      ? invoice.amountDueCents || 0n
                      : BigInt(totalCents),
                    // For new invoices, count pending payment schedules being created
                    // For existing invoices, use the persisted payment schedules
                    scheduleCount: invoice.id && invoice.id !== 'new-invoice'
                      ? paymentSchedules.length
                      : formPaymentSchedules.length,
                    tipAccountingVersion: invoice.tipAccountingVersion,
                    tipPaidCents: invoice.tipPaidCents || 0n,
                    tipDueCents: invoice.tipDueCents || 0n,
                  }}
                />
              )}
            </form.Subscribe>
          </section>

      {/* Revision reason input — shown when editing an existing invoice and line items have changed */}
      {!isNewInvoice && isEditing && (
        <form.Subscribe selector={(state) => state.values.items}>
          {(items) => {
            const formItemsAsLineItems = items.map(formItemToInvoiceLineItem)

            if (!hasInvoiceLineItemsChanged(invoice.lineItems, formItemsAsLineItems)) {
              return null
            }

            return (
              <div className="grid gap-2 rounded-2xl border bg-card p-5">
                <Label htmlFor="revision-reason">Revision Reason</Label>
                <form.Field name="revisionReason">
                  {(field) => (
                    <Input
                      id="revision-reason"
                      placeholder="Describe why line items were changed..."
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>
              </div>
            )
          }}
        </form.Subscribe>
      )}

      {!isNewInvoice && isEditing && activeCheckoutSessionCount > 0 && totalCents < Number(invoice.totalCents || 0n) && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          Invoice total reductions are blocked while a customer has an active checkout link.
        </div>
      )}

      {/* Payment schedule validation message */}
      <form.Subscribe selector={(state) => state.values.paymentSchedules}>
        {(paymentSchedules) => {
          if (paymentSchedules.length === 0) return null
          const existingPaymentSchedules = invoice.paymentSchedules || []
          const paymentSchedulesChanged = !isNewInvoice &&
            haveInvoicePaymentSchedulesChanged(existingPaymentSchedules, paymentSchedules)
          const paymentScheduleTotalMismatch = getPaymentScheduleTotalMismatch(paymentSchedules, totalCents)

          const effectivePaymentScheduleAdjustmentStrategy = paymentSchedulesChanged
            ? inferPaymentScheduleAdjustmentStrategy(
                existingPaymentSchedules,
                paymentSchedules,
                totalCents,
                paymentScheduleAdjustmentStrategy,
              )
            : paymentScheduleAdjustmentStrategy
          const paymentSchedulesChangedByUser = getPaymentSchedulesChangedByUser(
            paymentSchedulesChanged,
            existingPaymentSchedules,
            paymentSchedules,
            totalCents,
            effectivePaymentScheduleAdjustmentStrategy,
          )

          if (paymentScheduleTotalMismatch) {
            // Manual schedule edits (or new-invoice authoring) keep the destructive
            // styling since the user needs to fix the rows themselves. A mismatch
            // caused purely by a line-item/total change is resolved via the
            // adjustment dialog at save time, so it gets a softer, informational tone.
            const isManualEditMismatch = isNewInvoice || paymentSchedulesChangedByUser

            return (
              <div role="alert" className={cn(
                'flex items-start gap-2 rounded-lg border p-3 text-sm',
                isManualEditMismatch
                  ? 'border-destructive/40 bg-destructive/10 text-destructive'
                  : 'border-status-info-border bg-status-info-bg text-status-info-fg',
              )}>
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <p className="min-w-0">
                  {isManualEditMismatch ? (
                    <>
                      {paymentScheduleTotalMismatch.message}{' '}
                      <button
                        type="button"
                        className="rounded-sm font-medium underline underline-offset-2 hover:text-destructive/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30"
                        onClick={scrollToPaymentSection}
                      >
                        Go to {paymentScheduleSectionLabel}
                      </button>
                    </>
                  ) : (
                    "You'll choose how to collect the difference when you save."
                  )}
                </p>
              </div>
            )
          }

          if (!isNewInvoice && !paymentSchedulesChangedByUser) return null

          const activeCheckoutSessionCount = existingPaymentSchedules.filter(hasActiveCheckoutSession).length
          if (!isNewInvoice && activeCheckoutSessionCount > 0 && paymentSchedulesChangedByUser) {
            return (
              <div role="alert" className="flex items-start gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg p-3 text-sm text-status-warning-fg">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                A customer has an active checkout link. Wait for it to expire before changing the payment schedule.
              </div>
            )
          }

          return null
        }}
      </form.Subscribe>
        </div>

        <div className="min-w-0 xl:sticky xl:top-4 xl:max-h-[calc(100dvh-2rem)] xl:overflow-y-auto">
          <form.Subscribe selector={(state) => state.values}>
            {(values) => {
              const paymentScheduleTotalMismatch = getPaymentScheduleTotalMismatch(values.paymentSchedules, totalCents)
              const checklist = getInvoiceEditorChecklist({
                title: values.title,
                itemCount: values.items.length,
                paymentScheduleIssueMessage: paymentScheduleTotalMismatch?.message,
              })
              const discountLabel = values.discountType === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE && values.discountValue
                ? `Discount (${values.discountValue}%)`
                : 'Discount'
              const scheduleRows = normalizeInvoicePaymentSchedules(values.paymentSchedules, totalCents).map((schedule, index) => ({
                key: `${schedule.sequenceNumber}-${index}`,
                label: schedule.description || `Payment ${index + 1}`,
                amountCents: schedule.amountCents || 0,
              }))
              const amountDueCents = invoice.id && invoice.id !== 'new-invoice'
                ? Number(invoice.amountDueCents || 0n)
                : totalCents

              return (
                <QuoteBuilderSummaryRail
                  totalLabel="Invoice total"
                  checklistHeading="Before you save"
                  footerHint="Flip to Preview to see exactly what the customer receives."
                  totalCents={totalCents}
                  subtotalCents={subtotalCents}
                  discountAmountCents={discountAmountCents}
                  discountLabel={discountLabel}
                  taxAmountCents={taxAmountCents}
                  tipCents={tipCents}
                  tipLabel={values.enforcedTipPercentage
                    ? `Enforced tip (${values.enforcedTipPercentage}%)`
                    : undefined}
                  schedules={scheduleRows}
                  checklist={checklist}
                  onChecklistSelect={focusSection}
                  actions={railActions}
                  preview={
                    <InvoiceEditorPreview
                      recipientFirstName="the customer"
                      title={values.title}
                      lineItems={values.items.map((item, index) => ({
                        key: item.id || item.tempId || `item-${index}`,
                        name: item.name,
                        amountCents: calculateLineTotalCents(item.quantity, item.unitPriceCents),
                        quantity: item.quantity,
                        unitPriceCents: item.unitPriceCents,
                        unit: item.unit,
                        salesTaxRate: item.salesTaxRate,
                        isTaxable: item.isTaxable,
                        salesTaxCents: item.salesTaxCents,
                      }))}
                      subtotalCents={subtotalCents}
                      discountAmountCents={discountAmountCents}
                      discountLabel={discountLabel}
                      taxAmountCents={taxAmountCents}
                      tipCents={tipCents}
                      totalCents={totalCents}
                      amountDueCents={amountDueCents}
                      schedules={scheduleRows}
                    />
                  }
                />
              )
            }}
          </form.Subscribe>
        </div>
      </div>

      {/* Payment Management Dialogs */}
      <RecordPaymentDialog
        invoiceId={invoice.id}
        paymentSchedules={paymentSchedules}
        initialScheduleId={initialScheduleIdForPayment}
        open={showRecordPaymentDialog}
        onOpenChange={setShowRecordPaymentDialog}
        onSuccess={() => {
          setInitialScheduleIdForPayment(undefined)
        }}
      />

      <RefundDialog
        invoiceId={invoice.id}
        payment={selectedStripePaymentForRefund}
        open={showRefundDialog}
        onOpenChange={setShowRefundDialog}
        onSuccess={() => {
          setSelectedStripePaymentForRefund(null)
        }}
      />

      <VoidPaymentDialog
        invoiceId={invoice.id}
        payment={selectedManualPaymentForVoid}
        open={showVoidDialog}
        onOpenChange={setShowVoidDialog}
        onSuccess={() => {
          setSelectedManualPaymentForVoid(null)
        }}
      />

      <PaymentScheduleAdjustmentDialog
        open={showPaymentScheduleAdjustmentDialog}
        onOpenChange={setShowPaymentScheduleAdjustmentDialog}
        existingSchedules={paymentSchedules}
        previousTotalCents={Number(invoice.totalCents || 0n)}
        newTotalCents={totalCents}
        onSelect={handlePaymentScheduleAdjustmentDialogSelect}
        onCancel={() => setShowPaymentScheduleAdjustmentDialog(false)}
      />
    </form>
  )
}
